import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Activity, PhoneCall, MessageSquare, ArrowRightLeft, Mail, Upload,
  LogIn, Sparkles, Search, ExternalLink, RefreshCw, Download,
} from 'lucide-react';

const TYPE_LABEL = {
  appel: 'Appel',
  commentaire: 'Commentaire',
  statut: 'Changement de statut',
  email_rdv: 'Email RDV',
  email_prospection: 'Email prospection',
  email_doc: 'Email documentation',
  email: 'Emails',
  telephone: 'Téléphone modifié',
  modification: 'Modification fiche',
  assignation: 'Assignation',
  connexion: 'Connexion',
  validation_via_email: 'Validation utilisateur',
  rejet_via_email: 'Rejet utilisateur',
  import: 'Import Excel',
  enrichissement: 'Enrichissement',
};

const TYPE_COLOR = {
  appel: { bg: '#0055FF', icon: PhoneCall },
  commentaire: { bg: '#8B5CF6', icon: MessageSquare },
  statut: { bg: '#F97316', icon: ArrowRightLeft },
  email_rdv: { bg: '#10B981', icon: Mail },
  email_prospection: { bg: '#10B981', icon: Mail },
  email_doc: { bg: '#10B981', icon: Mail },
  email: { bg: '#10B981', icon: Mail },
  modification: { bg: '#71717A', icon: Activity },
  assignation: { bg: '#3B82F6', icon: Activity },
  telephone: { bg: '#0055FF', icon: PhoneCall },
  connexion: { bg: '#71717A', icon: LogIn },
  validation_via_email: { bg: '#10B981', icon: Sparkles },
  rejet_via_email: { bg: '#EF4444', icon: Sparkles },
  import: { bg: '#0055FF', icon: Upload },
  enrichissement: { bg: '#8B5CF6', icon: Sparkles },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (day.getTime() === today.getTime()) return `Aujourd'hui ${time}`;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function ActivityRow({ item, onOpenProspect }) {
  const palette = TYPE_COLOR[item.type] || { bg: '#71717A', icon: Activity };
  const Icon = palette.icon;
  let detail = item.contenu || '';
  if (item.type === 'statut' && item.metadata) {
    const { ancien, nouveau } = item.metadata || {};
    detail = `${ancien || '—'} → ${nouveau || '—'}`;
  }
  return (
    <div className="flex items-start gap-3 py-3 px-4 border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors" data-testid="activity-row">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${palette.bg}12`, color: palette.bg }}>
        <Icon className="w-[18px] h-[18px]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold" data-testid="activity-user">{item.user_nom}</span>
          <Badge variant="secondary" className="text-[10px] capitalize">{(item.user_role || '').replace('_', ' ')}</Badge>
          <span className="text-xs text-[#71717A]">{TYPE_LABEL[item.type] || item.type}</span>
          <span className="text-xs text-[#A1A1AA] ml-auto tabular-nums whitespace-nowrap">{formatDate(item.date)}</span>
        </div>
        {item.prospect_label && (
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-xs text-[#0055FF] font-medium truncate">{item.prospect_label}</span>
            {item.prospect_id && (
              <button
                onClick={() => onOpenProspect(item.prospect_id)}
                className="text-[#0055FF] hover:text-[#003BB8] transition-colors"
                data-testid="activity-open-prospect"
                title="Ouvrir la fiche"
              >
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
        {detail && (
          <p className="text-sm text-[#3F3F46] mt-1.5 leading-relaxed line-clamp-3 whitespace-pre-wrap">{detail}</p>
        )}
      </div>
    </div>
  );
}

export default function ActivitiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ user_id: 'all', type: 'all', period: '7d', q: '' });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const openProspect = (id) => navigate(`/prospects?prospect=${id}`);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/activities/users').then(r => setUsers(r.data.users || [])).catch(() => {});
  }, [isAdmin]);

  const fetchFeed = async (reset = false, pageOverride = null) => {
    setLoading(true);
    const targetPage = reset ? 1 : (pageOverride ?? page);
    const params = { page: targetPage, per_page: 50 };
    if (filters.user_id && filters.user_id !== 'all') params.user_id = filters.user_id;
    if (filters.type && filters.type !== 'all') params.type = filters.type;
    if (filters.period) params.period = filters.period;
    if (filters.q) params.q = filters.q;
    try {
      const { data } = await api.get('/activities/feed', { params });
      setItems(reset ? data.items : [...items, ...data.items]);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setPage(targetPage);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchFeed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.user_id, filters.type, filters.period, isAdmin]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchFeed(true);
  };

  const onExport = async () => {
    const params = new URLSearchParams();
    if (filters.user_id && filters.user_id !== 'all') params.set('user_id', filters.user_id);
    if (filters.type && filters.type !== 'all') params.set('type', filters.type);
    if (filters.period) params.set('period', filters.period);
    if (filters.q) params.set('q', filters.q);
    try {
      const { data } = await api.get(`/activities/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `activites_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* ignore */ }
  };

  const filtered = useMemo(() => items, [items]);

  if (!isAdmin) {
    return (
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-10 text-center">
          <p className="text-sm text-[#71717A]">Cet espace est réservé aux administrateurs.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="activities-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Activités</h1>
          <p className="text-sm text-[#71717A] mt-0.5">Flux unifié des actions sur l'application — appels, commentaires, statuts, emails, imports…</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onExport} data-testid="activities-export">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Exporter CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchFeed(true)} data-testid="activities-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rafraîchir
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Select value={filters.user_id} onValueChange={(v) => setFilters(f => ({ ...f, user_id: v }))}>
              <SelectTrigger data-testid="filter-user"><SelectValue placeholder="Utilisateur" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les utilisateurs</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom} <span className="text-[#A1A1AA] text-xs ml-1">· {u.role}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="appel">Appels</SelectItem>
                <SelectItem value="commentaire">Commentaires</SelectItem>
                <SelectItem value="statut">Changements de statut</SelectItem>
                <SelectItem value="email">Emails (RDV / prospection / doc)</SelectItem>
                <SelectItem value="modification">Modifications de fiche</SelectItem>
                <SelectItem value="assignation">Assignations</SelectItem>
                <SelectItem value="connexion">Connexions</SelectItem>
                <SelectItem value="import">Imports Excel</SelectItem>
                <SelectItem value="enrichissement">Enrichissements</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.period} onValueChange={(v) => setFilters(f => ({ ...f, period: v }))}>
              <SelectTrigger data-testid="filter-period"><SelectValue placeholder="Période" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Aujourd'hui</SelectItem>
                <SelectItem value="7d">7 derniers jours</SelectItem>
                <SelectItem value="30d">30 derniers jours</SelectItem>
                <SelectItem value="all">Tout l'historique</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input
                placeholder="Rechercher (prospect, contenu…)"
                value={filters.q}
                onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
                data-testid="filter-q"
              />
              <Button type="submit" size="icon" variant="secondary" aria-label="Rechercher">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Feed */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#71717A]">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#71717A]">Aucune activité sur cette période.</div>
          ) : (
            <>
              <div className="px-4 py-2.5 text-xs text-[#71717A] flex justify-between border-b border-[#F3F4F6] bg-[#FAFAFA]">
                <span data-testid="activities-count">{filtered.length} entrée(s) affichée(s) sur {total}</span>
              </div>
              <div data-testid="activities-list">
                {filtered.map(it => (
                  <ActivityRow key={`${it.source}-${it.id}`} item={it} onOpenProspect={openProspect} />
                ))}
              </div>
              {hasMore && (
                <div className="p-3 text-center border-t border-[#F3F4F6]">
                  <Button variant="outline" size="sm" onClick={() => fetchFeed(false, page + 1)} data-testid="activities-load-more">
                    Charger plus
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
